import React from "react";
import { useRouteError } from "react-router-dom";
import { FaCopy } from "react-icons/fa";
import {
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Card,
  CardHeader,
  Heading,
  CardBody,
  CardFooter,
  Button,
  useDisclosure,
  Collapse,
  VStack,
  Code,
  Icon,
  Box,
  Tooltip,
  Link,
  Divider,
  Center,
} from "@chakra-ui/react";

import { Layout } from "./PageLayouts";
import { useCustomToast } from "../hooks";

/**
 * ErrorResponse type is not given by react-router-dom
 */
type ErrorResponse = {
  data: unknown;
  status: number;
  statusText: string;
  message?: string;
  stack: string;
};

export function ErrorPage() {
  const error = useRouteError() as ErrorResponse;
  const { isOpen, onToggle } = useDisclosure();
  const copiedToast = useCustomToast(() => (
    <Alert status="success">Copied!</Alert>
  ));

  const copyErrorStack = React.useCallback(() => {
    navigator.clipboard.writeText(error.message + "\n" + error.stack);
    copiedToast();
  }, []);

  console.error({ error });

  return (
    <Layout variant="centered" align="center">
      <Card>
        <CardHeader as={VStack}>
          <Heading size="lg">Uh oh!</Heading>
          <Heading size="md">An error occured!</Heading>
        </CardHeader>
        <CardBody as={VStack}>
          <Alert status="error">
            <AlertIcon />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{error.message}</AlertDescription>
          </Alert>
          <Button onClick={onToggle} variant="primary">
            Show Stack Trace
          </Button>
          <Collapse in={isOpen} animateOpacity>
            <Box position="relative">
              <Code padding="0.75rem" borderRadius="0.5rem">
                {error.stack}
              </Code>
              <Tooltip hasArrow label="Copy Error Stack">
                <Button
                  variant="ghost"
                  position="absolute"
                  top="0.5rem"
                  right="0.5rem"
                  onClick={copyErrorStack}
                >
                  <Icon as={FaCopy} />
                </Button>
              </Tooltip>
            </Box>
          </Collapse>
        </CardBody>
        <Center>
          <Divider width="95%" />
        </Center>
        <CardFooter>
          If the problem persists, email
          <Link
            marginLeft="0.25rem"
            marginRight="0.25rem"
            href="mailto:support@ccv.brown.edu"
          >
            CCV Support
          </Link>
          with the issue!
        </CardFooter>
      </Card>
    </Layout>
  );
}
